import { create } from 'zustand';
import client from '../api/client';
import { getExcludedOptions, getIncludedPriceOptions, validateSelection } from '../utils/dependencyResolver';
import { calculateTotal } from '../utils/priceCalculator';

const config = window.astConfig || {};

const useConfigurationStore = create((set, get) => ({
    // Auth / lead state.
    isVerified: false,
    leadEmail: localStorage.getItem('ast_lead_email') || '',
    leadName: '',
    leadOrganization: '',

    // Data loading state.
    loading: true,
    categories: [],
    optionsByCategory: {},  // { categoryId: [options] }
    dependencies: {},       // { optionId: [deps] }
    optionMap: {},          // { optionId: option }
    templates: [],

    // Configuration state.
    configId: null,
    currentStep: 0,
    selectedOptions: {},    // { optionId: { ...option, quantity: 1 } }
    optionNotes: {},        // { optionId: "text value" }
    configTitle: '',
    configNotes: '',
    vatPercentage: 21,

    // General project info.
    contactName: '',
    contactEmail: localStorage.getItem('ast_lead_email') || '',
    organizationName: '',
    countryRegion: '',
    desiredQuantity: 1,
    desiredDeliveryDate: '',
    projectReference: '',

    // Computed from selections.
    excludedOptions: new Set(),
    includedPriceOptions: new Set(),
    validation: { valid: true, errors: [], warnings: [] },
    priceBreakdown: { subtotal: 0, vatAmount: 0, total: 0, lines: [] },

    // ============================================================
    // Lead / auth actions
    // ============================================================
    setVerified: (verified) => set({ isVerified: verified }),

    setLeadInfo: (email, name, organization) => {
        localStorage.setItem('ast_lead_email', email);
        set({ leadEmail: email, leadName: name, leadOrganization: organization });
    },

    checkVerification: async () => {
        const email = get().leadEmail;
        if (!email) return;

        try {
            const { data } = await client.get('/leads/status', { params: { email } });
            if (data.verified) {
                set({ isVerified: true });
            }
        } catch {
            // Not verified yet.
        }
    },

    // ============================================================
    // Data loading
    // ============================================================
    loadData: async () => {
        set({ loading: true });

        try {
            const [catRes, depRes, tplRes] = await Promise.all([
                client.get('/categories'),
                client.get('/dependencies'),
                client.get('/configurations/templates'),
            ]);

            // Filter out project info category — those fields are now in ProjectInfoStep.
            const categories = catRes.data.filter((cat) => cat.slug !== 'projectinformatie');
            const dependencies = depRes.data;
            const templates = tplRes.data;

            // Load options for each category.
            const optionsByCategory = {};
            const optionMap = {};

            const optionsPromises = categories.map((cat) =>
                client.get(`/categories/${cat.id}/options`).then(({ data }) => {
                    optionsByCategory[cat.id] = data;
                    data.forEach((opt) => {
                        optionMap[opt.id] = opt;
                    });
                })
            );

            await Promise.all(optionsPromises);

            // Pre-select default options.
            const selectedOptions = {};
            Object.values(optionsByCategory).flat().forEach((opt) => {
                if (opt.is_default === '1' || opt.is_default === 1) {
                    selectedOptions[opt.id] = { ...opt, quantity: 1 };
                }
            });

            // Pre-fill project info from lead data.
            const { leadEmail, leadName, leadOrganization, contactEmail, contactName, organizationName } = get();

            set({
                categories,
                optionsByCategory,
                dependencies,
                optionMap,
                templates,
                selectedOptions,
                loading: false,
                contactEmail: contactEmail || leadEmail || '',
                contactName: contactName || leadName || '',
                organizationName: organizationName || leadOrganization || '',
            });

            // Recalculate after loading.
            get().recalculate();
        } catch (err) {
            console.error('Failed to load configurator data:', err);
            set({ loading: false });
        }
    },

    // ============================================================
    // Navigation
    // ============================================================
    setStep: (step) => set({ currentStep: step }),
    nextStep: () => {
        const { currentStep, categories } = get();
        // Step 0 = ProjectInfo, steps 1..N = categories, last = summary.
        if (currentStep <= categories.length) {
            set({ currentStep: currentStep + 1 });
        }
    },
    prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 0) {
            set({ currentStep: currentStep - 1 });
        }
    },

    // ============================================================
    // Option selection
    // ============================================================
    toggleOption: (option) => {
        const { selectedOptions, optionsByCategory } = get();
        const optionId = Number(option.id);
        const newSelected = { ...selectedOptions };

        if (newSelected[optionId]) {
            // Deselect (unless required).
            if (option.is_required === '1' || option.is_required === 1) return;
            delete newSelected[optionId];
            // Clean up note.
            const { optionNotes } = get();
            if (optionNotes[optionId]) {
                const newNotes = { ...optionNotes };
                delete newNotes[optionId];
                set({ optionNotes: newNotes });
            }
        } else {
            // If option has a group, deselect others in same group + same category.
            if (option.option_group) {
                const categoryOptions = optionsByCategory[option.category_id] || [];
                categoryOptions.forEach((o) => {
                    if (o.option_group === option.option_group && newSelected[o.id]) {
                        delete newSelected[o.id];
                    }
                });
            }
            newSelected[optionId] = { ...option, quantity: 1 };
        }

        set({ selectedOptions: newSelected });
        get().recalculate();
    },

    setQuantity: (optionId, quantity) => {
        const { selectedOptions } = get();
        if (!selectedOptions[optionId]) return;

        const newSelected = { ...selectedOptions };
        newSelected[optionId] = { ...newSelected[optionId], quantity: Math.max(1, quantity) };

        set({ selectedOptions: newSelected });
        get().recalculate();
    },

    setOptionNote: (optionId, note) => {
        const { optionNotes } = get();
        set({ optionNotes: { ...optionNotes, [optionId]: note } });
    },

    // ============================================================
    // Recalculate derived state
    // ============================================================
    recalculate: () => {
        const { selectedOptions, dependencies, optionMap } = get();
        const selectedIds = Object.keys(selectedOptions).map(Number);

        const excludedOptions = getExcludedOptions(selectedIds, dependencies);
        const includedPriceOptions = getIncludedPriceOptions(selectedIds, dependencies);
        const validation = validateSelection(selectedIds, dependencies, optionMap);

        const optionsForCalc = selectedIds.map((id) => ({
            option_id: id,
            price: selectedOptions[id]?.price || 0,
            quantity: selectedOptions[id]?.quantity || 1,
            name: selectedOptions[id]?.name || '',
            sku: selectedOptions[id]?.sku || '',
            price_type: selectedOptions[id]?.price_type || 'fixed',
        }));

        const priceBreakdown = calculateTotal(optionsForCalc, includedPriceOptions, get().vatPercentage);

        set({ excludedOptions, includedPriceOptions, validation, priceBreakdown });
    },

    // ============================================================
    // Save / Load configuration
    // ============================================================
    saveConfiguration: async () => {
        const {
            configId, configTitle, configNotes, selectedOptions, optionNotes, priceBreakdown,
            contactName, contactEmail, organizationName, countryRegion,
            desiredQuantity, desiredDeliveryDate, projectReference,
        } = get();

        const lines = Object.values(selectedOptions).map((opt) => {
            const raw = optionNotes[opt.id];
            const notes = raw && typeof raw === 'object' ? JSON.stringify(raw) : (raw || '');
            return {
                option_id: Number(opt.id),
                quantity: opt.quantity || 1,
                notes,
            };
        });

        const projectInfo = {
            contact_name: contactName,
            contact_email: contactEmail,
            organization_name: organizationName,
            country_region: countryRegion,
            desired_quantity: desiredQuantity || 1,
            desired_delivery_date: desiredDeliveryDate,
            project_reference: projectReference,
        };

        try {
            if (configId) {
                // Update existing.
                await client.put(`/configurations/${configId}`, {
                    title: configTitle,
                    notes: configNotes,
                    total_price: priceBreakdown.subtotal,
                    ...projectInfo,
                });
                await client.post(`/configurations/${configId}/lines/bulk`, { lines });
            } else {
                // Create new.
                // Generate auto title: ORG-DATE or fallback.
                const autoTitle = organizationName
                    ? `${organizationName} — ${new Date().toLocaleDateString('nl-NL')}`
                    : `Offerte aanvraag ${new Date().toLocaleDateString('nl-NL')}`;

                const { data } = await client.post('/configurations', {
                    title: configTitle || autoTitle,
                    notes: configNotes,
                    status: 'draft',
                    total_price: priceBreakdown.subtotal,
                    ...projectInfo,
                });
                set({ configId: data.id });
                await client.post(`/configurations/${data.id}/lines/bulk`, { lines });
            }
            return true;
        } catch (err) {
            console.error('Failed to save configuration:', err);
            return false;
        }
    },

    loadConfiguration: async (id) => {
        try {
            const { data } = await client.get(`/configurations/${id}`);
            const { optionMap } = get();

            const selectedOptions = {};
            const optionNotes = {};
            (data.lines || []).forEach((line) => {
                const opt = optionMap[line.option_id];
                if (opt) {
                    selectedOptions[line.option_id] = {
                        ...opt,
                        quantity: line.quantity || 1,
                    };
                    if (line.notes) {
                        try {
                            optionNotes[line.option_id] = JSON.parse(line.notes);
                        } catch {
                            optionNotes[line.option_id] = line.notes;
                        }
                    }
                }
            });

            set({
                configId: data.id,
                configTitle: data.title,
                configNotes: data.notes || '',
                contactName: data.contact_name || '',
                contactEmail: data.contact_email || '',
                organizationName: data.organization_name || '',
                countryRegion: data.country_region || '',
                desiredQuantity: data.desired_quantity || 1,
                desiredDeliveryDate: data.desired_delivery_date || '',
                projectReference: data.project_reference || '',
                selectedOptions,
                optionNotes,
            });

            get().recalculate();
        } catch (err) {
            console.error('Failed to load configuration:', err);
        }
    },

    loadTemplate: async (templateId) => {
        try {
            const { data } = await client.post('/configurations/from-template', {
                template_id: templateId,
            });
            await get().loadConfiguration(data.id);
        } catch (err) {
            console.error('Failed to load template:', err);
        }
    },

    // ============================================================
    // Config metadata
    // ============================================================
    setConfigTitle: (title) => set({ configTitle: title }),
    setConfigNotes: (notes) => set({ configNotes: notes }),
    setProjectInfo: (field, value) => set({ [field]: value }),
}));

export default useConfigurationStore;
